import {
  CaretDownOutlined,
  PlusCircleFilled,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Select, message } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  fetchGroupMembers,
  fetchGroups,
  fetchUserGroups,
  removeFromGroups,
} from '../../api/groups';
import {
  fetchUsers,
  removeUserCredentialByType,
  updateUser,
  deletePermissionsByPrincipal,
} from '../../api/users';
import { UsersTable } from '../../components';
import { SearchInput } from '../../components/SearchInput';
import { GroupDetails, UserDetails } from '../../constants/types';
import {
  UseValueBreakpointConfigs,
  useValueBreakpoint,
} from '../../hooks/useValueBreakpoint';
import { arrayMove } from '../../utils/arrayMove';
import { getParentOrigin } from '../../utils/getKeycloakConfig';
import { Category, CategoryOptions, PageHeader } from '../styles';
import { StyledSelect } from './style';

const StyledSearchInput = styled(props => <SearchInput {...props} />)``;

const { Option } = Select;

const addBtnColClassName = {
  className: 'margin-left-auto',
};
const addBtnColSpan = {
  span: 24,
};

const breakpointConfig: UseValueBreakpointConfigs<any> = {
  lg: {
    addBtnCol: addBtnColClassName,
  },
  xl: {
    addBtnCol: addBtnColClassName,
  },
  xxl: {
    addBtnCol: addBtnColClassName,
  },
  md: {
    addBtnCol: addBtnColSpan,
  },
  sm: {
    addBtnCol: addBtnColSpan,
  },
  xs: {
    addBtnCol: addBtnColSpan,
  },
};

const Users = () => {
  const history = useHistory();
  const intl = useIntl();
  const parentOrigin = getParentOrigin();

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const { config } = useValueBreakpoint(breakpointConfig);
  const query = useQuery();
  const newUserId = query.get('newUser');
  const updatedUserId = query.get('updatedUser');
  const userToActivate = query.get('deactivatedUser');

  const [isLoading, setIsLoading] = useState(false);
  const [enabledUsersShown, setEnabledUsersShown] = useState<boolean>(true);
  const [currentUsers, setCurrentUsers] = useState<UserDetails[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserDetails[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupDetails[]>([]);
  const [userIdToHighlight, setUserIdToHighlight] = useState(
    newUserId || updatedUserId,
  );
  const [searchParam, setSearchParam] = useState<{
    search?: string;
    filter?: string;
    groupMember?: string[];
  }>({ search: undefined, filter: undefined, groupMember: undefined });

  const getGroupMember = useCallback(async (groupId: string) => {
    const groupMember: UserDetails[] = await fetchGroupMembers(
      groupId as string,
    );
    const userIds = groupMember.map(user => {
      return user.id;
    });
    return userIds as string[];
  }, []);

  const groupMemberFilter = useCallback(
    async (userIds: string[], users: UserDetails[]) => {
      const filteredGroupUser = users.filter(user => {
        return userIds.includes(user?.id as string);
      });
      return filteredGroupUser;
    },
    [],
  );

  const searchFilter = useCallback(
    async (searchValue: string, users: UserDetails[]) => {
      const usersList: UserDetails[] = users.filter(user => {
        if (
          `${user.firstName} ${user.lastName}`
            .toLocaleLowerCase()
            .indexOf(searchValue.toLocaleLowerCase().trim()) !== -1
        ) {
          return true;
        }
        if (
          user.email &&
          user.email
            ?.toLocaleLowerCase()
            .indexOf(searchValue.toLocaleLowerCase().trim()) !== -1
        ) {
          return true;
        }
        return false;
      });
      return usersList;
    },
    [],
  );

  const onSearch = useCallback(
    async (type: 'search' | 'filter', value) => {
      const obj: Record<string, string | string[] | undefined> = {};
      obj[type] = value;
      if (type === 'filter') {
        if (value) {
          const members = await getGroupMember(value as string);
          obj['groupMember'] = members;
        } else {
          obj['groupMember'] = undefined;
        }
      }
      setSearchParam(prevValue => {
        return {
          ...prevValue,
          ...obj,
        };
      });
    },
    [getGroupMember],
  );

  useEffect(() => {
    setIsLoading(true);

    if (userToActivate && enabledUsersShown !== false) {
      setEnabledUsersShown(false);
      setSearchParam(prevValue => {
        return {
          ...prevValue,
          search: userToActivate || undefined,
        };
      });
    } else {
      fetchUsers()
        .then(users => {
          if (enabledUsersShown) {
            const activeUser = users.filter(
              item =>
                item.enabled !== false &&
                (item?.['attributes']?.['account_end_date']?.[0]
                  ? moment(
                      parseInt(item['attributes']['account_end_date'][0], 10),
                    ).valueOf() > moment().valueOf()
                  : true),
            );
            setCurrentUsers(activeUser);
          } else {
            const deactivatedUser = users.filter(item => {
              return (
                !item.enabled ||
                (item?.['attributes']?.['account_end_date']?.[0]
                  ? moment(
                      parseInt(item['attributes']['account_end_date'][0], 10),
                    ).valueOf() < moment().valueOf()
                  : false)
              );
            });
            setCurrentUsers(deactivatedUser);
          }
        })
        .catch((error: any) => {
          message.error(error?.data?.error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return () => {
      setIsLoading(true);
      setCurrentUsers([]);
    };
  }, [enabledUsersShown, userToActivate]);

  useEffect(() => {
    fetchGroups().then(groups => {
      setGroupOptions(groups);
    });
  }, []);

  const onDeactivate = async (userId: string, username: string) => {
    const deactivatedUser = currentUsers.filter(
      (user: UserDetails) => user.id === userId,
    );

    const payload: Partial<UserDetails> = {
      ...(deactivatedUser[0] || {}),
      attributes: {
        ...(deactivatedUser[0]?.attributes || {}),
        account_end_date: moment().valueOf(),
      },
    };

    if (deactivatedUser.length > 0) {
      if (deactivatedUser[0]?.requiredActions?.includes('CONFIGURE_TOTP')) {
        payload.requiredActions = [
          ...(deactivatedUser[0].requiredActions || []).filter(
            (action: string) => action !== 'CONFIGURE_TOTP',
          ),
        ];
      }
      if (deactivatedUser[0]?.totp) {
        await removeUserCredentialByType(userId, 'otp');
      }
    }

    const userGroups = await fetchUserGroups(userId);
    const tenantAdminGroup = userGroups.find(
      group => group.name === 'tenant-admin',
    );
    const response = await deletePermissionsByPrincipal(username);
    if (!response) {
      message.warn(
        intl.formatMessage(
          {
            id: 'User.deactivated.removeGroup.warning',
          },
          {
            principal: username,
          },
        ),
      );
    }

    if (tenantAdminGroup) {
      await removeFromGroups(userId, tenantAdminGroup.id || '');
    }

    updateUser(userId, {
      ...payload,
    })
      .then(() => {
        setSearchParam({
          filter: undefined,
          groupMember: undefined,
          search: undefined,
        });
        setEnabledUsersShown(false);
        setUserIdToHighlight(userId);
      })
      .finally(() => {
        message.success(
          intl.formatMessage(
            {
              id: 'User.deactivated',
            },
            {
              firstName: deactivatedUser[0].firstName,
              lastName: deactivatedUser[0].lastName,
            },
          ),
        );
      });
  };

  const onReactivate = (userId: string): void => {
    // Redirect to edit user page of Spark UI if parentUrl exist
    // Normal history push if not

    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/users/edit/${userId}/reactiveUser`,
        },
        parentOrigin,
      );
    } else {
      history.push(`/users/edit/${userId}/reactiveUser`);
    }
  };

  const onSelectStatus = (isActivated: boolean) => {
    setSearchParam({
      filter: undefined,
      groupMember: undefined,
      search: undefined,
    });
    setEnabledUsersShown(isActivated);
    setUserIdToHighlight('');
  };

  useEffect(() => {
    const filterData = async () => {
      let usersList: UserDetails[] = currentUsers;
      if (searchParam.filter) {
        usersList = await groupMemberFilter(
          searchParam.groupMember || [],
          currentUsers,
        );
      }
      if (searchParam.search) {
        usersList = await searchFilter(searchParam.search, usersList);
      }

      if (userIdToHighlight) {
        const updatedUserIndex = usersList.findIndex(
          (user: UserDetails) => user.id === userIdToHighlight,
        );
        if (updatedUserIndex !== -1) {
          usersList = arrayMove(usersList, updatedUserIndex);
        }
      }

      setFilteredUsers(usersList);
    };

    filterData();
  }, [
    userIdToHighlight,
    currentUsers,
    searchParam,
    groupMemberFilter,
    searchFilter,
  ]);

  const existsOnFilteredValues = (userId: string | null) => {
    return filteredUsers.some((user: UserDetails) => user.id === userId);
  };

  // Redirect to create user page of Spark UI if parentUrl exist
  // Normal history push if not
  const redirectToCreateUser = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/users/create`,
        },
        parentOrigin,
      );
    } else {
      history.push('/users/create');
    }
  };

  return (
    <>
      <Row className="pt-28">
        <Col span={8} offset={8} data-testid="users-status-selection">
          <CategoryOptions>
            <Category
              selected={enabledUsersShown}
              onClick={() => {
                if (query.has('deactivatedUser')) {
                  query.delete('deactivatedUser');
                  history.replace({
                    search: query.toString(),
                  });
                }
                onSelectStatus(true);
              }}
              data-testid="users-status-active"
            >
              <FormattedMessage id="User.tab.activated" />
            </Category>
            <Category
              selected={!enabledUsersShown}
              onClick={() => {
                if (query.has('newUser')) {
                  query.delete('newUser');
                  history.replace({
                    search: query.toString(),
                  });
                }
                if (query.has('updatedUser')) {
                  query.delete('updatedUser');
                  history.replace({
                    search: query.toString(),
                  });
                }
                onSelectStatus(false);
              }}
              data-testid="users-status-inactive"
            >
              <FormattedMessage id="User.tab.Deactivated" />
            </Category>
          </CategoryOptions>
        </Col>
        <Col span={24}>
          <PageHeader gutter={[16, 8]} align="middle" wrap className="mxi-0">
            <Col xxl={{ span: 7 }} lg={{ span: 9 }} md={12} sm={12} xs={24}>
              <StyledSearchInput
                testId="users-search"
                placeholder={intl.formatMessage({
                  id: 'User.filter.search',
                })}
                value={searchParam.search}
                prefix={<SearchOutlined />}
                onChange={(value: string) => onSearch('search', value)}
                hideClearAll
              />
            </Col>

            {enabledUsersShown && (
              <>
                <Col xxl={{ span: 4 }} lg={{ span: 7 }} md={12} sm={12} xs={24}>
                  <StyledSelect
                    data-testid="filterByUserGroups"
                    placeholder={intl.formatMessage({
                      id: 'ApiKeys.filter.filterByUserGroups',
                    })}
                    value={searchParam.filter}
                    onSelect={async (value: string) =>
                      onSearch('filter', value !== '' ? value : undefined)
                    }
                    suffixIcon={<CaretDownOutlined />}
                    style={{ width: '100%' }}
                    listHeight={249}
                  >
                    {searchParam.filter !== undefined && (
                      <Option key="Default" value="" label="Clear Filter">
                        <span>Clear Filter</span>
                      </Option>
                    )}
                    {groupOptions.map((group: GroupDetails, index: number) => (
                      <Option key={index} value={group.id!} label={group.name}>
                        {group.name}
                      </Option>
                    ))}
                  </StyledSelect>
                </Col>
                <Col {...config?.addBtnCol}>
                  <Button
                    shape="round"
                    size="large"
                    type="default"
                    icon={<PlusCircleFilled />}
                    className="action-btn"
                    data-testid="users-create"
                    onClick={redirectToCreateUser}
                  >
                    <FormattedMessage id="User.newUser.btn" />
                  </Button>
                </Col>
              </>
            )}
          </PageHeader>
        </Col>

        <Col span={24} className="px-30">
          <UsersTable
            data-testid="users-table"
            loading={isLoading}
            users={filteredUsers}
            highlightFirstItem={existsOnFilteredValues(userIdToHighlight)}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
            enabledUsersShown={enabledUsersShown}
          />
        </Col>
      </Row>
    </>
  );
};

export default Users;
